import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';

import {GA4PropertyCard} from '../../business_units/accounts/GA4PropertyCard';
import {
    FIELD_MEDIUM, FIELD_SOURCE, GA4FiltersEditor, GA4_AVAILABLE_FIELDS, OPERATOR_CONTAINS,
} from '../../business_units/accounts/GA4FiltersEditor';
import {GOOGLE_CHANNEL} from '../../channels/channels';

/**
 * «Asociar Google Analytics» a una cuenta de la línea (mockup `#/mano/ga4`).
 *
 * Es el panel GA4 del clásico, reordenado: la propiedad primero y los filtros después. Dos pedidos de
 * Pablo del 04/08 (pestaña 30):
 *
 * - **Muy opcional**: la línea que abre este modal sólo aparece si la agencia TIENE GA4 conectado
 *   (lo decide el paso 4, no este modal).
 * - **«No asociar Analytics a esta línea» pesa lo mismo que «Guardar»**: sólo algunos lo quieren, y
 *   la salida tiene que ser tan clara como la entrada.
 *
 * Al asociar una cuenta de Google Ads, Dashbo sugiere el filtro de fuente/medio (`google` / `cpc`)
 * para que el tráfico coincida con la cuenta; en el clásico eso se armaba a mano.
 */
export function suggestedFilters(channel) {
    if (channel !== GOOGLE_CHANNEL) return [];
    return [
        {field: FIELD_SOURCE, operator: OPERATOR_CONTAINS, values: ['google']},
        {field: FIELD_MEDIUM, operator: OPERATOR_CONTAINS, values: ['cpc']},
    ];
}

export function Ga4AccountModal({config, analyticsAccounts, onSave, onClose}) {
    const {t} = useTranslation();
    const [propertyId, setPropertyId] = useState(config.ga4_ad_account_account_id || null);
    const [filters, setFilters] = useState(config.ga4_filters || []);

    const allAccounts = (analyticsAccounts || []).map(account => ({
        id: account.account_id,
        name: account.name,
        has_access: account.error_found === null || account.error_found === undefined,
    }));

    const chooseProperty = id => {
        setPropertyId(id);
        // La sugerencia se aplica sólo la primera vez: si ya había filtros, son del usuario.
        if (id && filters.length === 0) setFilters(suggestedFilters(config.channel));
    };

    const save = () => onSave({
        ga4_ad_account_account_id: propertyId,
        ga4_ad_account_id: null,
        ga4_filters: propertyId ? filters : [],
    });

    const dissociate = () => onSave({
        ga4_ad_account_account_id: null,
        ga4_ad_account_id: null,
        ga4_source: '',
        ga4_medium: '',
        ga4_filters: [],
    });

    return (
        // `scrollable`: con la vista previa el modal pasa los 800px de una laptop; scrollea el cuerpo
        // y el pie («Guardar») queda siempre a la vista (QA-VIS-ga4prev-17).
        <Modal isOpen scrollable toggle={onClose} className="lw-modal">
            <ModalHeader toggle={onClose}>
                {t('line_wizard.ga4_modal.title', {account: config.name, interpolation: {escapeValue: false}})}
            </ModalHeader>
            <ModalBody>
                <div className="modal-section">
                    <p className="lw-modal__sub">{t('line_wizard.ga4_modal.subtitle')}</p>
                    <label className="lw-modal__label">{t('line_wizard.ga4_modal.property_label')}</label>
                    <GA4PropertyCard selectedAccount={propertyId} allAccounts={allAccounts}
                                     onAccountChange={id => chooseProperty(id)}/>
                    {propertyId && (
                        <>
                            <label className="lw-modal__label">{t('line_wizard.ga4_modal.traffic_label')}</label>
                            {config.channel === GOOGLE_CHANNEL && (
                                <p className="lw-modal__help">{t('line_wizard.ga4_modal.suggested_help')}</p>
                            )}
                            {/* La cuenta de la fila: con ella la vista previa dice cuánto se puede
                                asignar a sus campañas y, en Meta, sugiere primero sus fuentes. */}
                            <GA4FiltersEditor filters={filters} availableFields={GA4_AVAILABLE_FIELDS} onChange={setFilters}
                                              propertyId={propertyId} adAccountId={config.ad_account_id}
                                              channel={config.channel}/>
                        </>
                    )}
                </div>
            </ModalBody>
            <ModalFooter className="lw-modal__footer-even">
                <button type="button" className="btn -alt -negative" onClick={dissociate}>
                    {t('line_wizard.ga4_modal.dont_associate')}
                </button>
                <button type="button" className="btn -alt" onClick={save} disabled={!propertyId}>
                    {t('line_wizard.ga4_modal.save')}
                </button>
            </ModalFooter>
        </Modal>
    );
}
